import React from 'react';
import { Menu } from '@ui/menus/Menu';
import { Icon } from '@ui/Icon';
import {
  closeMenu,
  useMenuPosition,
  useMenuStore,
} from '@client/hooks/use-menu.hooks';
import { formatBlueprintStoreyLevelLabel } from '@shared/helpers/design/blueprint-underlay.helpers';
import { useBlueprintUnderlayPageActions } from '../hooks/use-blueprint-underlay-page-actions.hook';
import { parseBlueprintUnderlayPageMenuId } from '../helpers/blueprint-underlay-page-menu.helpers';

/**
 * Kebab / right-click menu for one PDF underlay page.
 * Opened via {@link openMenu} with {@link blueprintUnderlayPageMenuId}.
 */
export const BlueprintUnderlayPageMenu: React.FC = () => {
  const menuId = useMenuStore((state) => state.id);
  const pageId = parseBlueprintUnderlayPageMenuId(menuId);
  const position = useMenuPosition(menuId);
  const {
    copyTargetLevelsFor,
    copyToStorey,
    isShownOnMap,
    removePage,
    setScale,
    toggleShownOnMap,
  } = useBlueprintUnderlayPageActions();

  if (!pageId || !position) return null;

  const shownOnMap = isShownOnMap(pageId);
  const copyTargetLevels = copyTargetLevelsFor(pageId);

  return (
    <Menu open anchorPosition={position} onClose={closeMenu} width={200}>
      <Menu.Item
        icon={<Icon category="mui" icon="Straighten" size="small" />}
        onClick={() => setScale(pageId)}
      >
        Set scale
      </Menu.Item>
      <Menu.Submenu
        label="Copy to storey"
        icon={<Icon category="mui" icon="ContentCopy" size="small" />}
        disabled={copyTargetLevels.length === 0}
      >
        {copyTargetLevels.map((level) => (
          <Menu.Item
            key={level}
            onClick={() => {
              void copyToStorey(pageId, level);
            }}
          >
            {formatBlueprintStoreyLevelLabel(level)}
          </Menu.Item>
        ))}
      </Menu.Submenu>
      <Menu.Item
        icon={
          <Icon
            category="mui"
            icon={shownOnMap ? 'VisibilityOff' : 'Visibility'}
            size="small"
          />
        }
        onClick={() => {
          void toggleShownOnMap(pageId);
        }}
      >
        {shownOnMap ? 'Hide' : 'Show'}
      </Menu.Item>
      <Menu.Item
        icon={<Icon category="mui" icon="Delete" size="small" />}
        onClick={() => {
          void removePage(pageId);
        }}
      >
        Delete
      </Menu.Item>
    </Menu>
  );
};
